<template>
<div class="wallet-draft-list container-fluid px-4 py-4">
	<!-- Page Header -->
	<div class="d-flex justify-content-between align-items-center mb-4">
		<div>
			<h1 class="h3 mb-0 text-gray-800">{{ $t('Wallet Draft List') }}</h1>
			<p class="text-muted mb-0 small">Manage and process pending wallet drafts into transaction history.</p>
		</div>
		<div>
			<button
				type="button"
				class="btn btn-primary d-flex align-items-center shadow-sm"
				:class="{ 'is-loading': isProcessing }"
				:disabled="isFetching || isProcessing"
				@click="onProcessDrafts"
			>
				<i class="material-icons mr-1">bolt</i>
				<span>{{ $t('Process Pending Drafts') }}</span>
			</button>
		</div>
	</div>

	<!-- Alert Process Result -->
	<div
		v-if="processResult"
		class="alert alert-success alert-dismissible fade show shadow-sm mb-4"
		role="alert"
	>
		<div class="d-flex align-items-center">
			<i class="material-icons mr-2 text-success">check_circle</i>
			<div>
				<strong>{{ $t('Processing Completed!') }}</strong>
				<span class="ml-2">
					Total: <b>{{ processResult.total }}</b> |
					Success: <b class="text-success">{{ processResult.total_success }}</b> |
					Failed: <b class="text-danger">{{ processResult.total_failed }}</b>
				</span>
			</div>
		</div>
		<button
			type="button"
			class="close"
			data-dismiss="alert"
			aria-label="Close"
			@click="processResult = null"
		>
			<span aria-hidden="true">&times;</span>
		</button>
	</div>

	<!-- Search & Filters Panel -->
	<div class="box p-3 mb-4 bg-white rounded shadow-sm border border-light">
		<div class="row align-items-center">
			<!-- Search -->
			<div class="col-md-3 mb-3 mb-md-0">
				<div class="input-group">
					<div class="input-group-prepend">
						<span class="input-group-text bg-white border-right-0">
							<i class="material-icons text-muted">search</i>
						</span>
					</div>
					<input
						type="text"
						class="form-control border-left-0"
						:placeholder="$t('Search description...')"
						v-model="keyword"
						@input="onKeywordChange"
					/>
				</div>
			</div>

			<!-- Status Filter -->
			<div class="col-md-2 mb-3 mb-md-0">
				<select
					class="form-control"
					v-model="selectedStatus"
					@change="onFilterChange"
				>
					<option value="">{{ $t('All Statuses') }}</option>
					<option value="pending">Pending</option>
					<option value="completed">Completed</option>
				</select>
			</div>

			<!-- Mutation Filter -->
			<div class="col-md-2 mb-3 mb-md-0">
				<select
					class="form-control"
					v-model="selectedMutation"
					@change="onFilterChange"
				>
					<option value="">{{ $t('All Mutations') }}</option>
					<option value="credit">Credit</option>
					<option value="debit">Debit</option>
				</select>
			</div>

			<!-- Type Filter -->
			<div class="col-md-3 mb-3 mb-md-0">
				<select
					class="form-control"
					v-model="selectedType"
					@change="onFilterChange"
				>
					<option value="">{{ $t('All Types') }}</option>
					<option value="top_up">Top Up</option>
					<option value="shipping_cashback">Shipping Cashback</option>
					<option value="shipping_cost">Shipping Cost COD</option>
					<option value="shipping_cost_non_cod">Shipping Cost Non-COD</option>
					<option value="refund">Refund</option>
					<option value="service_fee">Service Fee</option>
					<option value="transaction_fee">Transaction Fee</option>
				</select>
			</div>

			<!-- Limit Selector -->
			<div class="col-md-2 text-md-right">
				<select
					class="form-control d-inline-block w-auto"
					v-model="limit"
					@change="onLimitChange"
				>
					<option :value="10">10 / {{ $t('page') }}</option>
					<option :value="25">25 / {{ $t('page') }}</option>
					<option :value="50">50 / {{ $t('page') }}</option>
					<option :value="100">100 / {{ $t('page') }}</option>
				</select>
			</div>
		</div>
	</div>

	<!-- Main Content Area -->
	<div class="box p-0 bg-white rounded shadow-sm overflow-hidden border border-light">
		<div class="table-responsive">
			<table class="table table-hover table-striped mb-0 align-middle">
				<thead class="bg-light text-muted uppercase small">
					<tr>
						<th width="3%" class="text-center">#</th>
						<th width="20%">{{ $t('User') }}</th>
						<th width="12%">{{ $t('Type') }}</th>
						<th width="10%">{{ $t('Mutation') }}</th>
						<th width="12%" class="text-right">{{ $t('Value') }}</th>
						<th width="10%">{{ $t('Status') }}</th>
						<th>{{ $t('Description') }}</th>
						<th width="15%" class="text-right">{{ $t('Created / Completed At') }}</th>
					</tr>
				</thead>
				<tbody v-if="!isFetching && items.length > 0">
					<tr v-for="(item, index) in items" :key="item._id">
						<td class="text-center text-muted font-weight-bold">
							{{ (page - 1) * limit + index + 1 }}
						</td>
						<td>
							<div v-if="item.user" class="d-flex flex-column">
								<span class="font-weight-bold text-dark">{{ item.user.name || '-' }}</span>
								<span class="small text-secondary">@{{ item.user.username || '-' }}</span>
								<span class="small text-secondary font-italic">{{ item.user.email || '-' }}</span>
							</div>
							<div v-else class="text-muted small">
								ID: {{ item.user_id }}
							</div>
						</td>
						<td>
							<span class="badge badge-light border text-secondary text-capitalize px-2 py-1">
								{{ (item.type || '-').replace('_', ' ') }}
							</span>
						</td>
						<td>
							<span
								class="badge px-2 py-1 font-weight-bold text-uppercase"
								:class="item.mutation === 'credit' ? 'badge-success-light text-success' : 'badge-danger-light text-danger'"
							>
								{{ item.mutation }}
							</span>
						</td>
						<td class="text-right font-weight-bold" :class="item.mutation === 'credit' ? 'text-success' : 'text-danger'">
							{{ formatMoney(item.value || 0) }}
						</td>
						<td>
							<span
								class="badge badge-pill px-2.5 py-1 text-capitalize"
								:class="item.status === 'completed' ? 'badge-success' : 'badge-warning text-white'"
							>
								{{ item.status }}
							</span>
						</td>
						<td>
							<div class="text-dark small leading-relaxed">{{ item.description || '-' }}</div>
							<div v-if="item.info" class="text-muted mt-1 extra-small">Info: {{ item.info }}</div>
							<div v-if="item.created_by" class="text-muted mt-1 extra-small">
								By: <b>{{ item.created_by }}</b>
							</div>
						</td>
						<td class="text-right text-secondary small">
							<div class="d-flex flex-column align-items-end">
								<div>
									<span class="text-muted mr-1">Created:</span>
									{{ getFormattedDate(item.created_at) }}
								</div>
								<div v-if="item.completed_at">
									<span class="text-muted mr-1">Completed:</span>
									{{ getFormattedDate(item.completed_at) }}
								</div>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Empty State -->
		<div
			v-if="!isFetching && items.length === 0"
			class="py-5 text-center text-secondary border-top"
		>
			<i class="material-icons md-48 mb-2 text-muted">account_balance_wallet</i>
			<h5 class="mb-1">No Wallet Drafts Found</h5>
			<p class="text-muted mb-0 small">Try modifying your keyword search or active filter selections.</p>
		</div>

		<!-- Loader -->
		<div v-if="isFetching" class="py-5 border-top">
			<div class="is-loading dark large mx-auto"></div>
		</div>

		<!-- Table Footer & Pagination -->
		<div class="card-footer bg-white border-top px-4 py-3 d-flex flex-column flex-md-row align-items-center justify-content-between">
			<div class="text-muted small mb-3 mb-md-0">
				Showing items <b>{{ items.length }}</b> of page <b>{{ page }}</b>.
			</div>
			<div>
				<!-- Standard Pagination Component -->
				<pagination
					:pagination-pages="totalPages"
					:current-page="page"
					:last-page="totalPages"
					:is-loading="isFetching"
					@page-change="onPageChange"
					pagination-class="m-0 pagination-sm"
				/>
			</div>
		</div>
	</div>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getWalletDraft, processWalletDraft } from '@/api/admin';
import Pagination from '@/components/Pagination.vue';
import { formatMoney, formatDate, getAxiosErrorMessage } from '@/lib/helper';

export default {
	name: 'AdminWalletDraft',
	components: {
		Pagination,
	},
	data() {
		return {
			items: [],
			page: 1,
			limit: 10,
			totalPages: 1,
			sort_by: 'created_at',
			sort: 'desc',
			keyword: '',
			selectedStatus: '',
			selectedMutation: '',
			selectedType: '',
			isFetching: false,
			isProcessing: false,
			processResult: null,
		};
	},
	computed: {
		...mapGetters({
			user: 'user',
		}),
		apiParams() {
			const params = {
				page: this.page,
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
			};
			if (this.keyword.trim() !== '') {
				params.keyword = this.keyword.trim();
			}
			if (this.selectedStatus !== '') {
				params.status = this.selectedStatus;
			}
			if (this.selectedMutation !== '') {
				params.mutation = this.selectedMutation;
			}
			if (this.selectedType !== '') {
				params.type = this.selectedType;
			}
			return params;
		},
	},
	created() {
		this.fetchData();
	},
	methods: {
		fetchData() {
			this.isFetching = true;
			const callback = (response) => {
				if (Array.isArray(response.data)) {
					this.items = response.data;
					const meta = response.meta || {};
					const totalItems = meta.total || (this.items.length < this.limit && this.page === 1 ? this.items.length : this.page * this.limit + (this.items.length === this.limit ? 1 : 0));
					this.totalPages = Math.ceil(totalItems / this.limit) || 1;
				} else {
					this.items = [];
					this.totalPages = 1;
				}
				this.isFetching = false;
			};
			const errorCallback = (error) => {
				const message = getAxiosErrorMessage(error) || 'Failed to fetch wallet drafts';
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Error',
					text: message,
				});
				this.isFetching = false;
			};
			getWalletDraft(this.apiParams, callback, errorCallback);
		},
		onKeywordChange() {
			this.ooDelay(() => {
				this.page = 1;
				this.fetchData();
			}, 500);
		},
		onFilterChange() {
			this.page = 1;
			this.fetchData();
		},
		onLimitChange() {
			this.page = 1;
			this.fetchData();
		},
		onPageChange(newPage) {
			this.page = newPage;
			this.fetchData();
		},
		onProcessDrafts() {
			/* eslint-disable-next-line no-restricted-globals, no-alert */
			if (window.confirm('Are you sure you want to process all pending wallet drafts? This will update user balances.')) {
				this.isProcessing = true;
				this.processResult = null;
				const callback = (response) => {
					this.processResult = response.data || { total: 0, total_success: 0, total_failed: 0 };
					this.$notify({
						group: 'app',
						type: 'success',
						title: 'Success',
						text: 'Completed processing wallet drafts.',
					});
					this.isProcessing = false;
					this.page = 1;
					this.fetchData();
				};
				const errorCallback = (error) => {
					const message = getAxiosErrorMessage(error) || 'Failed to process pending drafts';
					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Error',
						text: message,
					});
					this.isProcessing = false;
				};
				processWalletDraft({}, callback, errorCallback);
			}
		},
		formatMoney(val) {
			return formatMoney(val, 'IDR');
		},
		getFormattedDate(dateString) {
			if (!dateString) return '-';
			const format = 'DD MMM YYYY, HH:mm';
			const timezone = this.user?.timezone || 'Asia/Jakarta';
			return formatDate(this.moment, dateString, format, timezone);
		},
	},
};
</script>

<style lang="scss" scoped>
.wallet-draft-list {
	.badge-success-light {
		background-color: rgba(40, 167, 69, 0.1);
	}
	.badge-danger-light {
		background-color: rgba(220, 53, 69, 0.1);
	}
	.extra-small {
		font-size: 0.75rem;
	}
	.align-middle {
		vertical-align: middle;
	}
}
</style>
